<div class="cerere-functii-assignment">
    <!-- Loading State -->
    <div *ngIf="loading" class="loading-container text-center p-3">
        <mat-spinner diameter="30"></mat-spinner>
        <p class="mt-2">Se încarcă asignările...</p>
    </div>

    <!-- No Assignments State -->
    <div *ngIf="!loading && (!assignments || assignments.length === 0)" class="no-assignments-container">
        <mat-card class="info-card">
            <mat-card-content class="text-center p-4">
                <mat-icon class="large-icon text-muted">assignment_ind</mat-icon>
                <h4 class="mt-3 mb-2">Nu există asignări</h4>
                <p class="text-muted mb-3">Această cerere nu are asignări de funcții configurate.</p>

                <div class="d-flex justify-content-center gap-2">
                    <button mat-raised-button color="primary" (click)="initializeFromTemplate()"
                        [disabled]="!cerere?.id_cerere_type">
                        <mat-icon>auto_fix_high</mat-icon>
                        Inițializează din tip cerere
                    </button>

                    <button mat-stroked-button color="primary" (click)="openManagementDialog()"
                        *ngIf="displayConfig.allowEdit">
                        <mat-icon>add</mat-icon>
                        Adaugă manual
                    </button>
                </div>

                <small class="text-muted d-block mt-2" *ngIf="!cerere?.id_cerere_type">
                    Tip cerere necunoscut - nu se poate inițializa automat
                </small>
            </mat-card-content>
        </mat-card>
    </div>

    <!-- Assignments Display -->
    <div *ngIf="!loading && assignments && assignments.length > 0" class="assignments-container">

        <!-- Header with Summary -->
        <div class="assignments-header d-flex justify-content-between align-items-center mb-3">
            <div class="summary-info">
                <h5 class="mb-1">
                    <mat-icon class="align-middle me-2">assignment_ind</mat-icon>
                    Asignări Funcții
                    <span class="badge badge-primary ms-2">{{ assignments.length }}</span>
                </h5>
                <div class="statistics" *ngIf="assignmentSummary?.statistics">
                    <small class="text-muted">
                        <span *ngIf="(assignmentSummary?.statistics?.draft ?? 0) > 0" class="me-3">
                            <span class="badge badge-secondary">{{ assignmentSummary?.statistics?.draft }}</span> Ciornă
                        </span>
                        <span *ngIf="(assignmentSummary?.statistics?.assigned ?? 0) > 0" class="me-3">
                            <span class="badge badge-success">{{ assignmentSummary?.statistics?.assigned }}</span>
                            Asignate
                        </span>
                        <span *ngIf="(assignmentSummary?.statistics?.completed ?? 0) > 0">
                            <span class="badge badge-info">{{ assignmentSummary?.statistics?.completed }}</span>
                            Finalizate
                        </span>
                    </small>
                </div>
            </div>

            <div class="header-actions d-flex gap-2" *ngIf="displayConfig.allowEdit">
                <button mat-icon-button matTooltip="Șterge toate asignările" (click)="clearAllAssignments()"
                    color="warn">
                    <mat-icon>clear_all</mat-icon>
                </button>

                <button mat-icon-button matTooltip="Gestionează asignări" (click)="openManagementDialog()">
                    <mat-icon>settings</mat-icon>
                </button>
            </div>
        </div>

        <!-- Compact Display Mode -->
        <div *ngIf="displayConfig.compactMode" class="assignments-compact">
            <div class="assignment-summary" [innerHTML]="assignmentSummary?.summary"></div>
            <!-- Fallback if summary is empty but assignments exist -->
            <div *ngIf="!assignmentSummary?.summary && assignments.length > 0" class="assignment-fallback">
                <div *ngFor="let assignment of assignments; let i = index" class="assignment-line">
                    {{ i + 1 }}. <strong>{{ assignment.functie?.name || 'Funcție necunoscută' }}</strong>
                    - {{ assignment.directie?.name || 'Direcție necunoscută' }}
                    <span *ngIf="assignment.compartiment_id">
                        / {{ assignment.compartiment?.name || 'Compartiment necunoscut' }}
                    </span>
                    <span *ngIf="assignment.user_id" class="text-success"> → {{ assignment.user?.full_name ||
                        assignment.user?.name || 'Utilizator necunoscut' }}</span>
                    <span *ngIf="!assignment.user_id" class="text-muted"> → Neasignat</span>
                </div>
            </div>
        </div>

        <!-- Detailed Display Mode -->
        <div *ngIf="!displayConfig.compactMode" class="assignments-detailed">
            <div *ngFor="let assignment of assignments; let i = index" class="assignment-item mb-3">
                <mat-card class="assignment-card">
                    <mat-card-content>
                        <div class="d-flex justify-content-between align-items-start">
                            <div class="assignment-info flex-grow-1">

                                <!-- Assignment Number and Status -->
                                <div class="d-flex align-items-center mb-2">
                                    <span class="assignment-number me-2">{{ i + 1 }}.</span>
                                    <span class="badge ms-auto"
                                        [ngClass]="getStatusCssClass(assignment.assignment_status)"
                                        *ngIf="displayConfig.showStatus">
                                        {{ getStatusDisplayText(assignment.assignment_status) }}
                                    </span>
                                    <span class="badge badge-warning ms-2"
                                        *ngIf="displayConfig.showDraftBadge && assignment.is_draft">
                                        Ciornă
                                    </span>
                                </div>

                                <!-- Function Information -->
                                <div class="function-info mb-2">
                                    <strong>Funcția:</strong>
                                    <span class="ms-1">{{ assignment.functie?.name || 'ID: ' + assignment.functie_id
                                        }}</span>
                                    <small class="text-muted ms-2" *ngIf="assignment.functie?.acronym">
                                        ({{ assignment.functie?.acronym }})
                                    </small>
                                </div>

                                <!-- Organization Information -->
                                <div class="organization-info mb-2">
                                    <strong>Organizare:</strong>
                                    <span class="ms-1">{{ assignment.directie?.name || 'ID: ' + assignment.directie_id
                                        }}</span>
                                    <span *ngIf="assignment.compartiment_id" class="text-muted ms-1">
                                        / {{ assignment.compartiment?.name || 'ID: ' + assignment.compartiment_id }}
                                    </span>
                                </div>

                                <!-- User Assignment -->
                                <div class="user-assignment" *ngIf="assignment.user_id">
                                    <strong>Utilizator asignat:</strong>
                                    <span class="ms-1">{{ assignment.user?.full_name || assignment.user?.name || 'ID: '
                                        + assignment.user_id }}</span>
                                    <small class="text-muted d-block" *ngIf="assignment.assigned_date">
                                        Asignat pe: {{ assignment.assigned_date | date:'dd.MM.yyyy HH:mm' }}
                                    </small>
                                </div>

                                <div class="no-user-assigned text-muted" *ngIf="!assignment.user_id">
                                    <em>Niciun utilizator asignat</em>
                                </div>

                            </div>

                            <!-- Assignment Actions -->
                            <div class="assignment-actions ms-3" *ngIf="displayConfig.allowAssignment">
                                <button mat-icon-button color="primary" matTooltip="Asignează utilizator"
                                    (click)="assignUserToFunction(assignment)" *ngIf="!assignment.user_id">
                                    <mat-icon>person_add</mat-icon>
                                </button>

                                <button mat-icon-button color="warn" matTooltip="Elimină asignarea"
                                    (click)="assignUserToFunction(assignment)" *ngIf="assignment.user_id">
                                    <mat-icon>person_remove</mat-icon>
                                </button>
                            </div>
                        </div>
                    </mat-card-content>
                </mat-card>
            </div>
        </div>
    </div>

    <!-- Management Dialog -->
    <div *ngIf="showManagementDialog" class="management-dialog-overlay">
        <div class="management-dialog">

            <!-- Dialog Header -->
            <div class="dialog-header d-flex justify-content-between align-items-center p-3 border-bottom">
                <h4 class="mb-0">
                    <mat-icon class="align-middle me-2">assignment_ind</mat-icon>
                    Gestionare Asignări Funcții
                    <small class="text-muted ms-2" *ngIf="cerere">{{ cerere.indoc_number }}</small>
                </h4>
                <button mat-icon-button (click)="closeManagementDialog()">
                    <mat-icon>close</mat-icon>
                </button>
            </div>

            <!-- Dialog Content -->
            <div class="dialog-content p-3" style="max-height: 70vh; overflow-y: auto;">

                <!-- Validation Errors -->
                <div *ngIf="validationErrors.length > 0" class="alert alert-danger">
                    <strong>Erori de validare:</strong>
                    <ul class="mb-0 mt-2">
                        <li *ngFor="let error of validationErrors">
                            {{ error.message }}
                        </li>
                    </ul>
                </div>

                <!-- Assignment Form -->
                <form [formGroup]="assignmentForm" class="assignment-form">
                    <div formArrayName="assignments">

                        <!-- Drag and Drop Container -->
                        <div cdkDropList (cdkDropListDropped)="onDrop($event)" class="assignments-container">

                            <!-- Assignment Item -->
                            <div *ngFor="let assignment of assignmentsArray.controls; let i = index" [formGroupName]="i"
                                cdkDrag class="assignment-item mb-4 p-3 border rounded">

                                <!-- Drag Handle -->
                                <div class="d-flex align-items-center mb-3">
                                    <div cdkDragHandle class="drag-handle me-3 cursor-move">
                                        <mat-icon class="text-muted">drag_handle</mat-icon>
                                    </div>
                                    <h4 class="mb-0 flex-grow-1">Asignare {{ i + 1 }}</h4>
                                    <button type="button" mat-icon-button color="warn" (click)="removeAssignment(i)"
                                        [disabled]="assignmentsArray.length <= 1" matTooltip="Șterge asignarea">
                                        <mat-icon>delete</mat-icon>
                                    </button>
                                </div>

                                <!-- Form Fields -->
                                <div class="row">
                                    <!-- Functie -->
                                    <div class="col-md-6">
                                        <mat-form-field class="w-100" appearance="outline">
                                            <mat-label>Funcția</mat-label>
                                            <mat-select formControlName="functie_id" required
                                                (openedChange)="onFunctieSelectClosed(i)">
                                                <mat-form-field class="px-2 w-100 sticky-input">
                                                    <input matInput [formControl]="functiiControls[i]"
                                                        (keydown)="$event.stopPropagation()"
                                                        (click)="$event.stopPropagation()" />
                                                </mat-form-field>

                                                <mat-option *ngIf="filteredFunctiiByIndex[i]?.length === 0" disabled>
                                                    <span>No results found</span>
                                                </mat-option>

                                                <mat-option *ngFor="let functie of filteredFunctiiByIndex[i]"
                                                    [value]="functie.id">
                                                    {{ functie.name }}
                                                    <span *ngIf="functie.acronym" class="text-muted">({{ functie.acronym
                                                        }})</span>
                                                </mat-option>
                                            </mat-select>
                                            <mat-error
                                                *ngIf="assignmentsArray.at(i)?.get('functie_id')?.hasError('required')">
                                                Funcția este obligatorie
                                            </mat-error>
                                        </mat-form-field>
                                    </div>

                                    <!-- Directie -->
                                    <div class="col-md-6">
                                        <mat-form-field class="w-100" appearance="outline">
                                            <mat-label>Direcția</mat-label>
                                            <mat-select formControlName="directie_id" required
                                                (openedChange)="onDirectieSelectClosed(i)">
                                                <mat-form-field class="px-2 w-100 sticky-input">
                                                    <input matInput [formControl]="directiiControls[i]"
                                                        (keydown)="$event.stopPropagation()"
                                                        (click)="$event.stopPropagation()" />
                                                </mat-form-field>

                                                <mat-option *ngIf="filteredDirectiiByIndex[i]?.length === 0" disabled>
                                                    <span>No results found</span>
                                                </mat-option>

                                                <mat-option *ngFor="let directie of filteredDirectiiByIndex[i]"
                                                    [value]="directie.id_directie">
                                                    {{ directie.name }}
                                                </mat-option>
                                            </mat-select>
                                            <mat-error
                                                *ngIf="assignmentsArray.at(i)?.get('directie_id')?.hasError('required')">
                                                Direcția este obligatorie
                                            </mat-error>
                                        </mat-form-field>
                                    </div>
                                </div>

                                <div class="row">
                                    <!-- Compartiment -->
                                    <div class="col-md-6">
                                        <mat-form-field class="w-100" appearance="outline">
                                            <mat-label>Compartimentul (opțional)</mat-label>
                                            <mat-select formControlName="compartiment_id"
                                                (openedChange)="onCompartimentDropdownOpened($event, i)"
                                                (selectionChange)="onCompartimentSelectClosed(i)">
                                                <mat-form-field class="px-2 w-100 sticky-input">
                                                    <input matInput [formControl]="compartimenteControls[i]"
                                                        (keydown)="$event.stopPropagation()"
                                                        (click)="$event.stopPropagation()" />
                                                </mat-form-field>

                                                <mat-option *ngIf="filteredCompartimenteByIndex[i]?.length === 0"
                                                    disabled>
                                                    <span>No results found</span>
                                                </mat-option>

                                                <mat-option [value]="-1">-- Fără compartiment --</mat-option>
                                                <mat-option *ngFor="let compartiment of filteredCompartimenteByIndex[i]"
                                                    [value]="compartiment.id_compartiment">
                                                    {{ compartiment.name }}
                                                </mat-option>
                                            </mat-select>
                                        </mat-form-field>
                                    </div>

                                    <!-- User -->
                                    <div class="col-md-6">
                                        <mat-form-field class="w-100" appearance="outline">
                                            <mat-label>Utilizatorul implicit (opțional)</mat-label>
                                            <mat-select formControlName="user_id"
                                                (openedChange)="onUserDropdownOpened($event, i)"
                                                (selectionChange)="onUserSelectClosed(i)">
                                                <mat-form-field class="px-2 w-100 sticky-input">
                                                    <input matInput [formControl]="usersControls[i]"
                                                        (keydown)="$event.stopPropagation()"
                                                        (click)="$event.stopPropagation()" />
                                                </mat-form-field>

                                                <mat-option *ngIf="filteredUsersByIndex[i]?.length === 0" disabled>
                                                    <span>No results found</span>
                                                </mat-option>

                                                <mat-option [value]="-1">-- Fără utilizator implicit --</mat-option>
                                                <mat-option *ngFor="let user of filteredUsersByIndex[i]"
                                                    [value]="user.id_user">
                                                    {{ user.full_name || user.name }}
                                                </mat-option>
                                            </mat-select>
                                        </mat-form-field>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <!-- Add New Assignment Button -->
                        <div class="text-center mt-3">
                            <button type="button" mat-stroked-button color="primary" (click)="addNewAssignment()">
                                <mat-icon>add</mat-icon>
                                Adaugă o nouă asignare
                            </button>
                        </div>
                    </div>
                </form>
            </div>

            <!-- Dialog Actions -->
            <div class="dialog-actions p-3 border-top d-flex justify-content-end gap-2">
                <button mat-button (click)="closeManagementDialog()" [disabled]="saving">
                    Anulează
                </button>

                <button mat-raised-button color="primary" (click)="saveAssignments()"
                    [disabled]="saving || assignmentForm.invalid">
                    <mat-icon *ngIf="saving" class="spin">hourglass_empty</mat-icon>
                    <mat-icon *ngIf="!saving">save</mat-icon>
                    {{ saving ? 'Se salvează...' : 'Salvează' }}
                </button>
            </div>
        </div>
    </div>
</div>